import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, MoreHorizontal, Pencil, Trash2, Loader2, Search, FolderKanban, Building2, Copy, Lock, ListTodo } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  // Tasks tab state
  const [clientTasksForProject, setClientTasksForProject] = useState([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [savingTaskSelection, setSavingTaskSelection] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    manager_id: '',
    client_id: '',
    allocated_hours: '',
    billable_rate: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, usersRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects`),
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/clients`).catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data.filter(u => {
        const userRoles = u.roles || (u.role ? [u.role] : []);
        return userRoles.includes('MANAGER') || userRoles.includes('ADMIN');
      }));
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingProject(null);
    setActiveTab('details');
    setClientTasksForProject([]);
    setSelectedTaskIds([]);
    setFormData({
      name: '',
      code: '',
      description: '',
      manager_id: '',
      client_id: '',
      allocated_hours: '',
      billable_rate: '',
      start_date: '',
      end_date: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setActiveTab('details');
    setClientTasksForProject([]);
    setSelectedTaskIds([]);
    setFormData({
      name: project.name,
      code: project.code,
      description: project.description || '',
      manager_id: project.manager_id || '',
      client_id: project.client_id || '',
      allocated_hours: project.allocated_hours?.toString() || '',
      billable_rate: project.billable_rate?.toString() || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
    });
    setDialogOpen(true);
    if (project.client_id) fetchTasksForProject(project);
  };

  const fetchTasksForProject = async (project) => {
    if (!project?.client_id) return;
    setTasksLoading(true);
    try {
      const [clientTasksRes, projectTasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${project.client_id}/tasks`),
        axios.get(`${API_URL}/api/projects/${project.id}/tasks`),
      ]);
      setClientTasksForProject(clientTasksRes.data);
      setSelectedTaskIds(projectTasksRes.data.map(t => t.id));
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleToggleTaskId = (taskId, checked) => {
    setSelectedTaskIds(prev =>
      checked ? [...prev, taskId] : prev.filter(id => id !== taskId)
    );
  };

  const handleSaveTaskSelection = async () => {
    if (!editingProject) return;
    setSavingTaskSelection(true);
    try {
      await axios.put(`${API_URL}/api/projects/${editingProject.id}/tasks`, {
        task_ids: selectedTaskIds,
      });
      await fetchData();
      toast.success('Task selection saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task selection');
    } finally {
      setSavingTaskSelection(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = { ...formData };
    if (!data.manager_id) data.manager_id = null;
    if (!data.client_id) data.client_id = null;
    data.allocated_hours = parseFloat(data.allocated_hours) || 0;
    data.billable_rate = parseFloat(data.billable_rate) || 0;
    if (!data.start_date) data.start_date = null;
    if (!data.end_date) data.end_date = null;

    try {
      if (editingProject) {
        await axios.put(`${API_URL}/api/projects/${editingProject.id}`, data);
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API_URL}/api/projects`, data);
        toast.success('Project created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.name}"? This will also delete all assignments and time entries.`)) return;

    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}`);
      toast.success('Project deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleDuplicate = async (project) => {
    try {
      await axios.post(`${API_URL}/api/projects/${project.id}/duplicate`);
      toast.success(`Project "${project.name}" duplicated successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate project');
    }
  };

  const handleToggleActive = async (project) => {
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}`, {
        is_active: !project.is_active,
      });
      toast.success(`Project ${project.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update project status');
    }
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">Manage projects and their settings</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-projects-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Allocated</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.map((project) => (
                <TableRow key={project.id} data-testid={`project-row-${project.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FolderKanban className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{project.name}</div>
                        {project.manager_name && (
                          <div className="text-xs text-muted-foreground">
                            Manager: {project.manager_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{project.code}</Badge>
                  </TableCell>
                  <TableCell>
                    {project.client_name ? (
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                        <span>{project.client_name}</span>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {project.allocated_hours ? `${project.allocated_hours}h` : '-'}
                  </TableCell>
                  <TableCell>
                    {project.start_date && project.end_date
                      ? `${project.start_date} - ${project.end_date}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {project.is_active ? (
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          Inactive
                        </Badge>
                      )}
                      {project.has_tasks && (
                        <Badge variant="secondary" className="text-xs gap-1">
                          <ListTodo className="w-3 h-3" />
                          Tasks
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`project-menu-${project.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(project)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(project)} data-testid={`duplicate-project-${project.id}`}>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(project)}>
                          {project.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(project)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredProjects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No projects found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Edit Project' : 'Create Project'}</DialogTitle>
            <DialogDescription>
              {editingProject ? 'Update project information and manage tasks' : 'Add a new project'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
              <TabsTrigger value="tasks" className="flex-1" disabled={!editingProject}>
                <ListTodo className="w-4 h-4 mr-1" />
                Tasks {editingProject && selectedTaskIds.length > 0 && `(${selectedTaskIds.length})`}
              </TabsTrigger>
            </TabsList>

            {/* ── Details Tab ── */}
            <TabsContent value="details">
              <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g., PRJ-001"
                    data-testid="project-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    data-testid="project-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="allocated_hours">Allocated Hours (Budget)</Label>
                  <Input
                    id="allocated_hours"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.allocated_hours}
                    onChange={(e) => setFormData({ ...formData, allocated_hours: e.target.value })}
                    placeholder="e.g., 500"
                    data-testid="project-allocated-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billable_rate">Billable Rate ($/hr)</Label>
                  <Input
                    id="billable_rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billable_rate}
                    onChange={(e) => setFormData({ ...formData, billable_rate: e.target.value })}
                    placeholder="e.g., 150.00"
                    data-testid="project-billable-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="manager">Manager</Label>
                  <Select
                    value={formData.manager_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="project-manager-select">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No manager</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client">Client</Label>
                  <Select
                    value={formData.client_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="project-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No client</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} ({client.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      data-testid="project-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      data-testid="project-end-date-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-project-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingProject ? 'Update' : 'Create'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>

            {/* ── Tasks Tab ── */}
            <TabsContent value="tasks" className="pt-2">
              <div className="space-y-4">
                {!editingProject?.client_id ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ListTodo className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm font-medium">No client assigned</p>
                    <p className="text-xs mt-1">Assign a client to this project to enable task tracking.</p>
                  </div>
                ) : tasksLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : clientTasksForProject.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ListTodo className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm font-medium">No tasks defined for this client yet</p>
                    <p className="text-xs mt-1">
                      Go to the <strong>Clients</strong> page and use &ldquo;Manage Tasks&rdquo; to define tasks for this client first.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Select which tasks from this client apply to this project. Employees will enter hours per task on the timesheet.
                    </p>
                    <div className="border rounded-md divide-y divide-border max-h-72 overflow-y-auto">
                      {clientTasksForProject.map((task) => (
                        <label
                          key={task.id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors ${
                            !task.is_active ? 'opacity-50' : ''
                          }`}
                        >
                          <Checkbox
                            checked={selectedTaskIds.includes(task.id)}
                            onCheckedChange={(checked) => handleToggleTaskId(task.id, checked)}
                            disabled={!task.is_active}
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-sm font-medium">{task.name}</span>
                            {task.code && (
                              <span className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{task.code}</span>
                            )}
                            {task.is_system && (
                              <span className="text-xs text-muted-foreground italic">catch-all</span>
                            )}
                            {!task.is_active && (
                              <span className="text-xs text-muted-foreground">(inactive)</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-xs text-muted-foreground">
                        {selectedTaskIds.length} of {clientTasksForProject.filter(t => t.is_active).length} active tasks selected
                      </span>
                      <Button
                        onClick={handleSaveTaskSelection}
                        disabled={savingTaskSelection}
                        size="sm"
                      >
                        {savingTaskSelection ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                        ) : 'Save Task Selection'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
